import { useEffect, useMemo, useState } from "react";
import { getHolidaySeason } from "../../distflowAPI/salesAnalyticsApi";
import SplitView from "./SplitView";
import "./HolidaySeason.css";

const PAGE = 25;
const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const compact = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", minimumFractionDigits: 0, maximumFractionDigits: 1 });
const money = (value) => value == null ? "—" : currency.format(Number(value));
const date = (value, year = true) => value ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", ...(year ? { year: "numeric" } : {}), timeZone: "UTC" }).format(new Date(`${value}T00:00:00Z`)) : "";
const range = (start, end) => `${date(start, false)} – ${date(end)}`;
const signedPct = (value) => {
  if (value == null) return null;
  const number = Number(value);
  return `${number > 0 ? "+" : number < 0 ? "−" : ""}${Math.abs(number).toFixed(1)}%`;
};
const LEAD_WEEKS = [2, 3, 4, 6, 8];
const STATUS_ORDER = ["missing", "slipped", "grew", "new", "steady"];
const VIEWS = [
  { key: "customers", label: "Customers", noun: "Customer", action: "Open details for" },
  { key: "products", label: "Products", noun: "Product", action: "Open on the Products tab:" },
  { key: "categories", label: "Categories", noun: "Category", action: "Focus the page on" },
];

function WeekBars({ weeks, finished }) {
  const max = Math.max(1, ...weeks.flatMap((week) => [Number(week.sales || 0), Number(week.previous_sales || 0)]));
  return <div className="season-weeks" role="list" aria-label="Sales by week of the season">
    {weeks.map((week) => {
      const upcoming = week.sales == null;
      const height = (value) => `${Math.max(0, Number(value || 0)) / max * 100}%`;
      return <div role="listitem" key={week.start_date} className={`season-week season-week--${week.kind}${upcoming ? " is-upcoming" : ""}`}
        title={`${week.label}\nThis season ${range(week.start_date, week.end_date)}: ${upcoming ? "not reached yet" : money(week.sales)}${!upcoming && !week.complete ? " (so far)" : ""}\nLast season ${range(week.previous_start_date, week.previous_end_date)}: ${money(week.previous_sales)}`}>
        <div className="season-week-bars" aria-hidden="true">
          <span className="season-week-bar season-week-bar--current" style={{ height: upcoming ? "100%" : height(week.sales) }} />
          <span className="season-week-bar season-week-bar--previous" style={{ height: height(week.previous_sales) }} />
        </div>
        <strong>{week.label}</strong>
        <span className="season-week-values">{upcoming ? "upcoming" : compact.format(Number(week.sales))}{!upcoming && !week.complete && !finished ? " so far" : ""}<small>last {compact.format(Number(week.previous_sales))}</small></span>
      </div>;
    })}
  </div>;
}

export default function HolidaySeason({ territoryId, filters = [], onSelect, openKey, onOpenCustomer, onOpenProduct, renderCustomerPanel }) {
  const [season, setSeason] = useState("");
  const [year, setYear] = useState("");
  const [leadWeeks, setLeadWeeks] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState("customers");
  const [status, setStatus] = useState(null);
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE);
  const filterSignature = filters.join("|");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    Promise.resolve()
      .then(() => getHolidaySeason({
        season: season || undefined, year: year || undefined, lead_weeks: leadWeeks || undefined,
        territory_id: territoryId, filters: filterSignature ? filterSignature.split("|") : [],
      }, { signal: controller.signal }))
      .then((result) => { if (!controller.signal.aborted) { setData(result || null); setStatus(null); setVisible(PAGE); } })
      .catch((requestError) => {
        if (controller.signal.aborted || requestError?.code === "ERR_CANCELED") return;
        setData(null);
        setError("The season comparison could not be loaded.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [season, year, leadWeeks, territoryId, filterSignature]);

  const group = data?.[view];
  const activeStatus = status ?? (group?.counts?.missing ? "missing" : "all");
  const rows = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    return (group?.rows || []).filter((row) =>
      (activeStatus === "all" || row.status === activeStatus) &&
      (!term || `${row.name} ${row.sku || ""}`.toLocaleLowerCase().includes(term)));
  }, [group, activeStatus, query]);

  if (!data && !loading && !error) return null;
  const currentView = VIEWS.find((item) => item.key === view) || VIEWS[0];
  const open = (row) => {
    if (view === "customers") onOpenCustomer?.(row.key);
    else if (view === "products") onOpenProduct?.(row.key);
    else onSelect?.(row);
  };
  const panel = view === "customers" && openKey && renderCustomerPanel ? renderCustomerPanel : null;
  const summary = data?.summary;
  const labels = data?.status_labels || {};
  const finished = data?.status === "finished";
  const notStarted = data?.status === "not_started";
  const selectedYear = data?.hebrew_year;
  const yearOptions = selectedYear ? [selectedYear + 1, selectedYear, selectedYear - 1, selectedYear - 2] : [];
  const missingLabel = finished ? "Didn’t order this season" : labels.missing;

  return <SplitView panel={panel}><section className="season" aria-label="Yom Tov season" aria-busy={loading}>
    <div className="sales-analytics-section-head">
      <div><span className="sales-analytics-eyebrow">This Yom Tov season vs last</span><h2>Seasons</h2></div>
      <span className="sales-analytics-section-aside">Aligned by Hebrew date. Weeks count back from the first day of Yom Tov, so each has one Shabbat in both years.</span>
    </div>
    {error && <p className="season-error" role="alert">{error}</p>}
    {loading && !data && <p className="season-empty">Loading the season…</p>}
    {data && <div className={`season-card${loading ? " season-card--refreshing" : ""}`}>
      <div className="season-controls">
        <label><span>Season</span>
          <select value={data.season} onChange={(event) => { setSeason(event.target.value); setYear(""); }}>
            {Object.entries(data.seasons || {}).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label><span>Year</span>
          <select value={selectedYear} onChange={(event) => { setSeason(data.season); setYear(Number(event.target.value)); }}>
            {yearOptions.map((option) => <option key={option} value={option}>{option} vs {option - 1}</option>)}
          </select>
        </label>
        <label><span>Weeks before Yom Tov</span>
          <select value={data.lead_weeks} onChange={(event) => { setSeason(data.season); setYear(selectedYear); setLeadWeeks(Number(event.target.value)); }}>
            {LEAD_WEEKS.map((option) => <option key={option} value={option}>{option} weeks</option>)}
          </select>
        </label>
        <span className={`season-status season-status--${data.status}`}>
          {notStarted ? `Starts ${date(data.current.start_date)}` : finished ? "Finished" : `In progress · day ${data.days_in} of ${data.season_days}`}
        </span>
      </div>

      <p className="season-dates">
        <strong>{data.label} {data.hebrew_year}</strong> {range(data.current.start_date, data.current.end_date)} (Yom Tov from {date(data.current.holiday_start, false)})
        {" "}vs <strong>{data.label} {data.previous.hebrew_year}</strong> {range(data.previous.start_date, data.previous.end_date)}
      </p>

      <div className="season-metrics">
        <div className="season-metric season-metric--featured">
          <span>{finished ? "This season" : "This season so far"}</span>
          <strong>{notStarted ? "—" : money(summary.sales)}</strong>
          {!notStarted && <small className={Number(summary.sales_change_percent) < 0 ? "is-decline" : "is-gain"}>{signedPct(summary.sales_change_percent) ?? "No baseline"} vs last season{finished ? "" : " at this point"}</small>}
        </div>
        <div className="season-metric">
          <span>{finished ? "Last season" : "Last season at this point"}</span>
          <strong>{notStarted ? "—" : money(summary.previous_sales)}</strong>
          {!notStarted && !finished && <small>up to {date(data.previous.same_point_date)}</small>}
        </div>
        <div className="season-metric">
          <span>Last season total</span>
          <strong>{money(summary.last_season_total)}</strong>
          <small>{summary.last_season_customers} customers</small>
        </div>
        <div className="season-metric">
          <span>Customers</span>
          <strong>{notStarted ? "—" : summary.customers}</strong>
          {!notStarted && <small>{summary.previous_customers} {finished ? "last season" : "at this point last season"}</small>}
        </div>
      </div>

      <WeekBars weeks={data.weeks || []} finished={finished} />
      <div className="season-legend" aria-hidden="true"><span><i className="season-swatch--current" />{data.hebrew_year}</span><span><i className="season-swatch--previous" />{data.previous.hebrew_year}</span><span><i className="season-swatch--yomtov" />Yom Tov</span></div>

      <div className="season-lists">
        <div className="sa-segmented" role="group" aria-label="Season breakdown">
          {VIEWS.map((item) => <button type="button" key={item.key} aria-pressed={view === item.key}
            className={view === item.key ? "is-selected" : ""} onClick={() => { setView(item.key); setStatus(null); setQuery(""); setVisible(PAGE); }}>{item.label}</button>)}
        </div>
        <div className="sa-chips" role="group" aria-label="Filter by change">
          {STATUS_ORDER.filter((key) => group?.counts?.[key]).map((key) => <button type="button" key={key} aria-pressed={activeStatus === key}
            className={activeStatus === key ? "is-selected" : ""} onClick={() => { setStatus(key); setVisible(PAGE); }}>
            {key === "missing" ? missingLabel : labels[key]}<b>{group.counts[key]}</b></button>)}
          <button type="button" aria-pressed={activeStatus === "all"} className={activeStatus === "all" ? "is-selected" : ""} onClick={() => { setStatus("all"); setVisible(PAGE); }}>All<b>{group?.total || 0}</b></button>
        </div>
        <div className="sa-tools">
          <input type="search" aria-label={`Search ${view}`} placeholder={`Search ${view}`} value={query} onChange={(event) => { setQuery(event.target.value); setVisible(PAGE); }} />
        </div>
        {activeStatus === "missing" && !finished && rows.length > 0 && <p className="sa-note">{notStarted
          ? "Bought last season. Worth a call before the season starts."
          : "Bought by this point last season (or later in the season) but haven’t ordered yet."}</p>}
        {rows.length ? <div className="sa-table-scroll"><table className="sa-list-table" aria-label={`Season ${view}`}>
          <thead><tr><th scope="col" className="sa-col-name">{currentView.noun}</th><th scope="col">Change</th>
            <th scope="col" className="sa-num">{finished ? "This season" : "This season so far"}</th><th scope="col" className="sa-num">{finished ? "Last season" : "Last season at this point"}</th>
            <th scope="col" className="sa-num">Difference</th><th scope="col" className="sa-num sa-col-secondary">Last season total</th></tr></thead>
          <tbody>{rows.slice(0, visible).map((row) => <tr key={row.key} className={openKey === row.key ? "is-open" : ""} onClick={() => open(row)}>
            <th scope="row" className="sa-col-name">
              <button type="button" className="sa-row-link" title={`${currentView.action} ${row.name}`} aria-current={openKey === row.key ? "true" : undefined}
                onClick={(event) => { event.stopPropagation(); open(row); }}>{row.name}</button>
              {row.sku && <small>{row.sku}</small>}
            </th>
            <td className="sa-cell-plain" data-label="Change"><span className={`sa-badge sa-badge--${row.status}`}>{row.status === "missing" ? missingLabel : labels[row.status]}</span></td>
            <td className="sa-num" data-label={finished ? "This season" : "So far"}>{money(row.sales)}</td>
            <td className="sa-num" data-label={finished ? "Last season" : "Last season at this point"}>{money(row.previous_sales)}</td>
            <td className={`sa-num ${Number(row.change_amount) < 0 ? "sa-bad" : Number(row.change_amount) > 0 ? "sa-good" : ""}`} data-label="Difference">
              {Number(row.change_amount) > 0 ? "+" : Number(row.change_amount) < 0 ? "−" : ""}{money(Math.abs(Number(row.change_amount)))}
              {row.change_percent != null && <small>{signedPct(row.change_percent)}</small>}</td>
            <td className="sa-num sa-col-secondary" data-label="Last season total">{money(row.last_season_total)}</td>
          </tr>)}</tbody>
        </table></div> : <p className="sa-list-empty">Nothing in this group.</p>}
        <div className="sa-list-foot">
          <span>{group && group.total > group.rows.length ? `Showing the ${group.rows.length} biggest by last season’s total, out of ${group.total}. ` : ""}
            {view === "customers" ? "Click a customer for their details." : view === "products" ? "Click a product to open it on the Products tab." : "Click a category to focus the page on it."}</span>
          {rows.length > visible && <button type="button" className="sa-link-button" onClick={() => setVisible(visible + PAGE)}>Show {Math.min(PAGE, rows.length - visible)} more</button>}
        </div>
      </div>
    </div>}
  </section></SplitView>;
}
